import { Button } from "./ui/button";
import { User, X, ShieldOff, Phone, FileText, Sparkles } from "lucide-react";

/**
 * Modal shown to *guest* users after they click "Send Request" on a booking
 * form, BEFORE the quote is submitted to the backend.
 *
 * - `Continue as Guest` (left button): proceeds with the submission as-is.
 * - `Sign in with Google` (right button): the parent submits the quote as a
 *   guest first (preserving form data + creating the booking), then triggers
 *   the Google OAuth redirect. After login the user's personal area will
 *   automatically pick up the new booking via the email match in
 *   `GET /api/quotes`.
 *
 * The modal is *non-blocking*: clicking the backdrop or X closes it without
 * doing anything (the user has to make an explicit choice on the buttons).
 */
export default function GuestRegistrationPrompt({ open, onClose, onContinueAsGuest, onSignInAndSubmit, submitting }) {
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="guest-register-prompt"
    >
      <div
        className="relative w-full max-w-lg rounded-sm border border-[#C5A059]/30 bg-[#0F0F0F] p-6 sm:p-8 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute right-3 top-3 text-white/40 hover:text-white transition-colors"
          data-testid="guest-prompt-close"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-[#C5A059]/15 border border-[#C5A059]/30 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-[#C5A059]" />
          </div>
          <div>
            <h2 className="text-xl text-white font-semibold tracking-wide">Create your account?</h2>
            <p className="text-xs text-white/50 mt-0.5">Takes 10 seconds with Google — never miss a trip detail</p>
          </div>
        </div>

        {/* Benefits */}
        <ul className="space-y-3 mb-6 mt-5">
          <li className="flex items-start gap-3" data-testid="guest-benefit-area">
            <User className="w-4 h-4 text-[#C5A059] mt-0.5 flex-shrink-0" />
            <div>
              <div className="text-sm text-white">Personal area</div>
              <div className="text-xs text-white/50">See all your bookings, past and upcoming, in one place</div>
            </div>
          </li>
          <li className="flex items-start gap-3" data-testid="guest-benefit-cancel">
            <ShieldOff className="w-4 h-4 text-[#C5A059] mt-0.5 flex-shrink-0" />
            <div>
              <div className="text-sm text-white">Online cancellation</div>
              <div className="text-xs text-white/50">Cancel directly from the app, up to 24 hours before pickup</div>
            </div>
          </li>
          <li className="flex items-start gap-3" data-testid="guest-benefit-driver">
            <Phone className="w-4 h-4 text-[#C5A059] mt-0.5 flex-shrink-0" />
            <div>
              <div className="text-sm text-white">Direct driver contact</div>
              <div className="text-xs text-white/50">Get your driver's name and phone before pickup</div>
            </div>
          </li>
          <li className="flex items-start gap-3" data-testid="guest-benefit-history">
            <FileText className="w-4 h-4 text-[#C5A059] mt-0.5 flex-shrink-0" />
            <div>
              <div className="text-sm text-white">Trip history & receipts</div>
              <div className="text-xs text-white/50">Full record of every ride — perfect for expense reports</div>
            </div>
          </li>
        </ul>

        {/* Reassurance */}
        <p className="text-[11px] text-white/40 mb-4 text-center">
          Your booking will be submitted either way — registration is optional.
        </p>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            type="button"
            onClick={onContinueAsGuest}
            disabled={submitting}
            className="flex-1 bg-transparent border border-white/20 text-white/70 hover:bg-white/5 hover:text-white py-3 h-auto rounded-sm"
            data-testid="guest-continue-btn"
          >
            {submitting ? "Sending…" : "Continue as guest"}
          </Button>
          <Button
            type="button"
            onClick={onSignInAndSubmit}
            disabled={submitting}
            className="flex-1 btn-gold py-3 h-auto rounded-sm font-semibold tracking-wide"
            data-testid="guest-signin-btn"
          >
            {submitting ? "Sending…" : "Send & sign in with Google"}
          </Button>
        </div>
      </div>
    </div>
  );
}
